import { FormEvent, useEffect, useState } from "react";
import {
  attachProofRecommendation,
  generateProofPacket,
  generatePulsePacket,
  getProofPackets,
  getPulsePackets,
  transitionClientStage,
  updateClient,
  updateNode,
  updatePartner
} from "../lib/api";
import type { ClientRecord, ClientStage, NodeRecord, PartnerRecord, Permissions, ProofPacket, ProofRecommendation, PulsePacket, WarmEntity, WarmEntityType } from "../lib/types";
import { Icon } from "./Icon";
import { entityName } from "./Pages";

const clientStages: ClientStage[] = ["warm_prospect", "discovery_booked", "proposal_sent", "paid", "subscribed", "website_created", "active", "paused", "churned", "lost"];

export function LeadDetail({
  selected,
  permissions,
  onUpdated
}: {
  selected: { type: WarmEntityType; entity: WarmEntity } | null;
  permissions: Permissions | null;
  onUpdated?: () => Promise<void> | void;
}) {
  const [followUpNotes, setFollowUpNotes] = useState("");
  const [lastContactedAt, setLastContactedAt] = useState("");
  const [lastContactChannel, setLastContactChannel] = useState("");
  const [lastContactSummary, setLastContactSummary] = useState("");
  const [nextFollowUpAt, setNextFollowUpAt] = useState("");
  const [referralAskSent, setReferralAskSent] = useState(false);
  const [referralPaid, setReferralPaid] = useState(false);
  const [stage, setStage] = useState<ClientStage>("warm_prospect");
  const [services, setServices] = useState("");
  const [mainGoal, setMainGoal] = useState("");
  const [mainGoalSource, setMainGoalSource] = useState<"inferred" | "confirmed" | "unknown">("inferred");
  const [additionalGoals, setAdditionalGoals] = useState("");
  const [businessStageNote, setBusinessStageNote] = useState("");
  const [primaryStoryMetric, setPrimaryStoryMetric] = useState("");
  const [secondaryStoryMetrics, setSecondaryStoryMetrics] = useState("");
  const [ga4PropertyId, setGa4PropertyId] = useState("");
  const [ga4MeasurementId, setGa4MeasurementId] = useState("");
  const [ga4PropertyUrl, setGa4PropertyUrl] = useState("");
  const [cloudflareAnalyticsUrl, setCloudflareAnalyticsUrl] = useState("");
  const [cloudflareZoneId, setCloudflareZoneId] = useState("");
  const [searchConsolePropertyUrl, setSearchConsolePropertyUrl] = useState("");
  const [searchConsoleVerificationStatus, setSearchConsoleVerificationStatus] = useState<"unknown" | "not_started" | "pending" | "verified" | "blocked">("unknown");
  const [sitemapSubmitted, setSitemapSubmitted] = useState(false);
  const [dataCollectionStartDate, setDataCollectionStartDate] = useState("");
  const [proofPackets, setProofPackets] = useState<ProofPacket[]>([]);
  const [proofRecommendations, setProofRecommendations] = useState<ProofRecommendation[]>([]);
  const [pulsePackets, setPulsePackets] = useState<PulsePacket[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const entity = selected?.entity;
    setFollowUpNotes(String(entity?.follow_up_notes || ""));
    setLastContactedAt(String(entity?.last_contacted_at || ""));
    setLastContactChannel(String(entity?.last_contact_channel || ""));
    setLastContactSummary(String(entity?.last_contact_summary || ""));
    setNextFollowUpAt(String(entity?.next_follow_up_at || ""));
    setReferralAskSent(Boolean(entity?.referral_ask_sent));
    setStage(selected?.type === "client" ? (entity as ClientRecord).stage : "warm_prospect");
    const client = selected?.type === "client" ? (entity as ClientRecord) : null;
    setReferralPaid(Boolean(client?.referral_paid));
    setServices(listText(client?.services_json));
    setMainGoal(String(client?.main_goal || ""));
    setMainGoalSource(client?.main_goal_source || "inferred");
    setAdditionalGoals(listText(client?.additional_goals_json));
    setBusinessStageNote(String(client?.business_stage_note || ""));
    setPrimaryStoryMetric(String(client?.primary_story_metric || ""));
    setSecondaryStoryMetrics(listText(client?.secondary_story_metrics_json));
    setGa4PropertyId(String(client?.ga4_property_id || ""));
    setGa4MeasurementId(String(client?.ga4_measurement_id || ""));
    setGa4PropertyUrl(String(client?.ga4_property_url || ""));
    setCloudflareAnalyticsUrl(String(client?.cloudflare_analytics_url || ""));
    setCloudflareZoneId(String(client?.cloudflare_zone_id || ""));
    setSearchConsolePropertyUrl(String(client?.search_console_property_url || ""));
    setSearchConsoleVerificationStatus(client?.search_console_verification_status || "unknown");
    setSitemapSubmitted(Boolean(client?.sitemap_submitted));
    setDataCollectionStartDate(String(client?.data_collection_start_date || ""));
    setProofPackets([]);
    setProofRecommendations([]);
    setPulsePackets([]);
    setMessage("");
  }, [selected]);

  useEffect(() => {
    const client = selected?.type === "client" ? (selected.entity as ClientRecord) : null;
    if (!client || !permissions?.canChangeClientStage) return;
    getProofPackets({ clientId: client.id })
      .then((data) => setProofPackets(data.proofPackets))
      .catch(() => setProofPackets([]));
    getPulsePackets({ clientId: client.id })
      .then((data) => setPulsePackets(data.pulsePackets))
      .catch(() => setPulsePackets([]));
    if (client.stage === "warm_prospect") {
      getProofPackets({ leadClientId: client.id })
        .then((data) => setProofRecommendations(data.recommendations || []))
        .catch(() => setProofRecommendations([]));
    }
  }, [selected, permissions?.canChangeClientStage]);

  if (!selected) {
    return (
      <aside className="lead-detail empty">
        <Icon name="user" size={24} />
        <h2>Select a record</h2>
        <p>Open a client, partner, or node to inspect follow-up state and make allowed updates.</p>
      </aside>
    );
  }

  const { type, entity } = selected;
  const owner = permissions?.canChangeClientStage;

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const input = { followUpNotes, lastContactedAt, lastContactChannel, lastContactSummary, nextFollowUpAt, referralAskSent };
      if (type === "client") await updateClient(entity.id, input);
      if (type === "partner") await updatePartner(entity.id, input);
      if (type === "node") await updateNode(entity.id, input);
      setMessage("Changes saved.");
      await onUpdated?.();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not save changes.");
    } finally {
      setSaving(false);
    }
  }

  async function changeStage() {
    if (type !== "client" || !owner) return;
    setSaving(true);
    setMessage("");
    try {
      await transitionClientStage(entity.id, stage);
      setMessage("Client stage updated.");
      await onUpdated?.();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not update stage.");
    } finally {
      setSaving(false);
    }
  }

  async function saveReferralPaid(nextValue: boolean) {
    if (type !== "client" || !owner) return;
    setSaving(true);
    setMessage("");
    try {
      await updateClient(entity.id, { referralPaid: nextValue });
      setReferralPaid(nextValue);
      setMessage(nextValue ? "Referral marked paid." : "Referral marked unpaid.");
      await onUpdated?.();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not update referral payout.");
    } finally {
      setSaving(false);
    }
  }

  async function saveClientTracking(event: FormEvent) {
    event.preventDefault();
    if (type !== "client" || !owner) return;
    setSaving(true);
    setMessage("");
    try {
      await updateClient(entity.id, {
        services,
        mainGoal,
        mainGoalSource,
        additionalGoals,
        businessStageNote,
        primaryStoryMetric,
        secondaryStoryMetrics,
        ga4PropertyId,
        ga4MeasurementId,
        ga4PropertyUrl,
        cloudflareAnalyticsUrl,
        cloudflareZoneId,
        searchConsolePropertyUrl,
        searchConsoleVerificationStatus,
        sitemapSubmitted,
        dataCollectionStartDate
      });
      setMessage("Client tracking saved.");
      await onUpdated?.();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not save client tracking.");
    } finally {
      setSaving(false);
    }
  }

  async function generateProofForClient() {
    if (type !== "client" || !owner) return;
    setSaving(true);
    setMessage("");
    try {
      const result = await generateProofPacket(entity.id);
      setProofPackets((current) => [result.proofPacket, ...current]);
      setMessage("Proof packet queued for review.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not generate proof.");
    } finally {
      setSaving(false);
    }
  }

  async function generatePulseForClient() {
    if (type !== "client" || !owner) return;
    setSaving(true);
    setMessage("");
    try {
      const result = await generatePulsePacket(entity.id);
      setPulsePackets((current) => [result.pulsePacket, ...current]);
      setMessage("Pulse packet queued for review.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not generate Pulse.");
    } finally {
      setSaving(false);
    }
  }

  async function attachRecommendation(recommendation: ProofRecommendation) {
    if (type !== "client" || !owner) return;
    setSaving(true);
    setMessage("");
    try {
      await attachProofRecommendation(recommendation.id);
      setProofRecommendations((current) => current.map((item) => (item.id === recommendation.id ? { ...item, status: "attached" } : item)));
      setMessage("Proof attached to warm lead.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not attach proof.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <aside className="lead-detail">
      <div className="detail-header">
        <span className="detail-kind">{type}</span>
        <h2>{entityName(entity)}</h2>
        <span className="detail-status">{statusLabel(type, entity)}</span>
      </div>

      <div className="detail-identity">
        <DetailLine icon="phone" value={entity.phone_number || entity.email || "No primary contact"} />
        <DetailLine icon="sheet" value={identityLine(type, entity)} />
        <div className="detail-tags">
          <span>{entity.city_area || "No city"}</span>
          <span>ID: {entity.id.slice(0, 18)}</span>
        </div>
      </div>

      <div className="owner-box">
        <span className="initial">{(entity.created_by_user_id || "BL").slice(0, 2).toUpperCase()}</span>
        <div>
          <small>Created by</small>
          <strong>{entity.created_by_user_id || "owner"}</strong>
        </div>
        <span className="owner-note">{shortDate(entity.updated_at)}</span>
      </div>

      {type === "client" && owner ? (
        <section className="quick-edit">
          <h3>Owner stage control</h3>
          <label>
            Client stage
            <select value={stage} onChange={(event) => setStage(event.target.value as ClientStage)}>
              {clientStages.map((item) => <option key={item} value={item}>{label(item)}</option>)}
            </select>
          </label>
          <button type="button" onClick={changeStage} disabled={saving}>Change stage</button>
        </section>
      ) : null}

      {type === "client" && owner && hasReferrer(entity as ClientRecord) ? (
        <section className="quick-edit">
          <h3>Referral payout</h3>
          <p className="muted">
            Referrer: {(entity as ClientRecord).referrer_name || "see source"}
            {" · "}
            {referralPayoutState(entity as ClientRecord, referralPaid)}
          </p>
          <label className="checkbox-row">
            <input type="checkbox" checked={referralPaid} onChange={(event) => saveReferralPaid(event.target.checked)} disabled={saving} />
            Referral paid
          </label>
        </section>
      ) : null}

      {type === "client" && owner ? (
        <form className="quick-edit tracking-edit" onSubmit={saveClientTracking}>
          <h3>Client tracking</h3>
          <label>
            Services provided
            <textarea value={services} onChange={(event) => setServices(event.target.value)} placeholder={"Website\nBooking automation\nReporting"} />
          </label>
          <label>
            Main goal
            <textarea value={mainGoal} onChange={(event) => setMainGoal(event.target.value)} placeholder="What does this client most want the work to achieve?" />
          </label>
          <label>
            Goal source
            <select value={mainGoalSource} onChange={(event) => setMainGoalSource(event.target.value as "inferred" | "confirmed" | "unknown")}>
              <option value="inferred">Inferred</option>
              <option value="confirmed">Confirmed</option>
              <option value="unknown">Unknown</option>
            </select>
          </label>
          <label>
            Additional goals
            <textarea value={additionalGoals} onChange={(event) => setAdditionalGoals(event.target.value)} placeholder="One goal per line" />
          </label>
          <label>
            Business stage note
            <textarea value={businessStageNote} onChange={(event) => setBusinessStageNote(event.target.value)} placeholder="Established, new launch, scaling, recovering missed leads..." />
          </label>
          <label>
            Primary story metric
            <textarea value={primaryStoryMetric} onChange={(event) => setPrimaryStoryMetric(event.target.value)} placeholder="The one proof point the client will care about most." />
          </label>
          <label>
            Secondary story metrics
            <textarea value={secondaryStoryMetrics} onChange={(event) => setSecondaryStoryMetrics(event.target.value)} placeholder="One metric per line" />
          </label>
          <label>
            GA4 property ID
            <input value={ga4PropertyId} onChange={(event) => setGa4PropertyId(event.target.value)} placeholder="properties/123456789" />
          </label>
          <label>
            GA4 measurement ID
            <input value={ga4MeasurementId} onChange={(event) => setGa4MeasurementId(event.target.value)} placeholder="G-XXXXXXXXXX" />
          </label>
          <label>
            GA4 report link
            <input value={ga4PropertyUrl} onChange={(event) => setGa4PropertyUrl(event.target.value)} placeholder="https://analytics.google.com/..." />
          </label>
          <label>
            Cloudflare analytics link
            <input value={cloudflareAnalyticsUrl} onChange={(event) => setCloudflareAnalyticsUrl(event.target.value)} placeholder="https://dash.cloudflare.com/..." />
          </label>
          <label>
            Cloudflare zone ID
            <input value={cloudflareZoneId} onChange={(event) => setCloudflareZoneId(event.target.value)} placeholder="Zone ID for automated reporting" />
          </label>
          <label>
            Search Console link
            <input value={searchConsolePropertyUrl} onChange={(event) => setSearchConsolePropertyUrl(event.target.value)} placeholder="https://search.google.com/search-console/..." />
          </label>
          <label>
            Search Console status
            <select value={searchConsoleVerificationStatus} onChange={(event) => setSearchConsoleVerificationStatus(event.target.value as "unknown" | "not_started" | "pending" | "verified" | "blocked")}>
              <option value="unknown">Unknown</option>
              <option value="not_started">Not started</option>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="blocked">Blocked</option>
            </select>
          </label>
          <label>
            Data collection start
            <input value={dataCollectionStartDate} onChange={(event) => setDataCollectionStartDate(event.target.value)} placeholder="2026-07-05" />
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={sitemapSubmitted} onChange={(event) => setSitemapSubmitted(event.target.checked)} />
            Sitemap submitted
          </label>
          <button type="submit" disabled={saving}>{saving ? "Saving" : "Save tracking"}</button>
        </form>
      ) : null}

      {type === "client" && owner ? (
        <section className="quick-edit proof-pulse-panel">
          <h3>Proof and Pulse</h3>
          <div className="mini-status-grid">
            <div>
              <small>Proof status</small>
              <strong>{proofPackets[0] ? label(proofPackets[0].status) : "No packet"}</strong>
            </div>
            <div>
              <small>Latest Pulse</small>
              <strong>{pulsePackets[0] ? label(pulsePackets[0].status) : "No draft"}</strong>
            </div>
          </div>
          {stage === "warm_prospect" ? (
            <div className="recommendation-stack">
              <small>Best proof to use</small>
              {proofRecommendations.length === 0 ? <span>No matching proof yet.</span> : null}
              {proofRecommendations.slice(0, 2).map((recommendation) => (
                <button type="button" className="recommendation-card" key={recommendation.id} onClick={() => attachRecommendation(recommendation)} disabled={saving || recommendation.status === "attached"}>
                  <strong>{recommendation.proof_client_name || recommendation.title}</strong>
                  <span>{recommendation.reason}</span>
                  <em>{recommendation.status === "attached" ? "Attached" : "Attach proof"}</em>
                </button>
              ))}
            </div>
          ) : (
            <div className="detail-action-row">
              <button type="button" onClick={generateProofForClient} disabled={saving || !proofReady(stage)}>Generate proof</button>
              <button type="button" onClick={generatePulseForClient} disabled={saving}>Generate Pulse</button>
            </div>
          )}
        </section>
      ) : null}

      <form className="quick-edit" onSubmit={save}>
        <h3>Contact and follow-up</h3>
        <label>
          Last contact date
          <input type="date" value={lastContactedAt} onChange={(event) => setLastContactedAt(event.target.value)} />
        </label>
        <label>
          Contact channel
          <select value={lastContactChannel} onChange={(event) => setLastContactChannel(event.target.value)}>
            <option value="">Not recorded</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="call">Call</option>
            <option value="email">Email</option>
            <option value="in_person">In person</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label>
          Last contact message or summary
          <textarea value={lastContactSummary} onChange={(event) => setLastContactSummary(event.target.value)} placeholder="What was sent or discussed? Keep this concise enough for the next person to continue." />
        </label>
        <label>
          Next follow-up
          <input value={nextFollowUpAt} onChange={(event) => setNextFollowUpAt(event.target.value)} placeholder="2026-07-10" />
        </label>
        <label>
          Next action / follow-up notes
          <textarea value={followUpNotes} onChange={(event) => setFollowUpNotes(event.target.value)} placeholder="What should happen next?" />
        </label>
        <label className="checkbox-row">
          <input type="checkbox" checked={referralAskSent} onChange={(event) => setReferralAskSent(event.target.checked)} />
          Referral ask sent
        </label>
        <button type="submit" disabled={saving}>{saving ? "Saving" : "Save follow-up"}</button>
        {message ? <span className="form-message">{message}</span> : null}
      </form>

      <section className="detail-section">
        <h3>Context</h3>
        <p>{contextText(type, entity)}</p>
        {"internal_notes" in entity && entity.internal_notes ? (
          <div className="internal-note">
            <strong>Internal note</strong>
            <span>{entity.internal_notes}</span>
          </div>
        ) : null}
      </section>
    </aside>
  );
}

function DetailLine({ icon, value }: { icon: "phone" | "sheet"; value: string }) {
  return (
    <div className="detail-line">
      <Icon name={icon} />
      <strong>{value}</strong>
    </div>
  );
}

function statusLabel(type: WarmEntityType, entity: WarmEntity) {
  if (type === "client") return label((entity as ClientRecord).stage);
  return label((entity as PartnerRecord | NodeRecord).relationship_stage);
}

function identityLine(type: WarmEntityType, entity: WarmEntity) {
  if (type === "client") {
    const client = entity as ClientRecord;
    return label(client.service_type || "website");
  }
  if (type === "partner") {
    const partner = entity as PartnerRecord;
    return label(partner.partner_type || "business");
  }
  const node = entity as NodeRecord;
  return [label(node.node_type || "connector"), node.organization_name].filter(Boolean).join(" - ");
}

function contextText(type: WarmEntityType, entity: WarmEntity) {
  if (type === "client") {
    const client = entity as ClientRecord;
    return client.candidate_justification || client.follow_up_notes || client.referrer_name || "No client context has been added yet.";
  }
  if (type === "partner") {
    const partner = entity as PartnerRecord;
    return partner.referral_fit_notes || partner.candidate_justification || partner.follow_up_notes || "No partner context has been added yet.";
  }
  const node = entity as NodeRecord;
  return node.community_context || node.referral_fit_notes || node.candidate_justification || "No node context has been added yet.";
}

function label(value?: string | null) {
  return String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(" ") || "None";
}

function listText(value?: string | null) {
  if (!value) return "";
  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed)) return parsed.map((item) => String(item || "").trim()).filter(Boolean).join("\n");
  } catch {
    return value;
  }
  return value;
}

function proofReady(stage: ClientStage) {
  return ["paid", "subscribed", "website_created", "active"].includes(stage);
}

function shortDate(value?: string | null) {
  if (!value) return "Not updated";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}

const REFERRAL_SOURCE_KINDS = new Set(["client_referral", "partner_referral", "node_referral"]);

function hasReferrer(client: ClientRecord): boolean {
  return Boolean(client.referrer_name) || REFERRAL_SOURCE_KINDS.has(client.source_kind || "");
}

function formatAmount(pence?: number | null): string {
  if (pence == null) return "£75";
  return `£${(pence / 100).toFixed(2).replace(/\.00$/, "")}`;
}

function referralPayoutState(client: ClientRecord, referralPaid: boolean): string {
  const amount = formatAmount(client.referral_amount_pence);
  if (referralPaid) return `Paid ${amount}${client.referral_paid_at ? ` on ${shortDate(client.referral_paid_at)}` : ""}`;
  if (client.referral_payout_reminded_at) return `${amount} owed (reminder sent ${shortDate(client.referral_payout_reminded_at)})`;
  return `${amount} on first paid or subscribed`;
}
